// src/helpers/agentsHelper.js
import { get, post, patch } from './apiHelper';

/**
 * Helper para los agentes de IA optimizados
 * Incluye todos los nuevos endpoints con LangChain y optimizaciones
 */

// ===== ANÁLISIS OPTIMIZADO =====
export const agentsOptimized = {
    // Análisis básico optimizado
    analyzeData: async (userId) => {
        return await get(`agents/optimized/analyze/${userId}`);
    },

    // Análisis con IA optimizado
    analyzeDataWithAI: async (userId) => {
        return await get(`agents/optimized/analyze-with-ai/${userId}`);
    },

    // Análisis de métrica específica
    analyzeMetric: async (userId, metric) => {
        return await get(`agents/optimized/analyze-metric/${userId}/${metric}`);
    },

    // Análisis específico con contexto
    specificAnalysis: async (userId, metric) => {
        return await get(`agents/optimized/specific-analysis/${userId}/${metric}`);
    }
};

// ===== ESTRATEGIAS OPTIMIZADAS =====
export const strategiesOptimized = {
    // Definir estrategia con contexto
    defineStrategy: async (analysis, objective, context) => {
        return await post('agents/optimized/strategize', {
            analysis,
            objective,
            context
        });
    },

    // Validar estrategia con documentación
    validateStrategy: async (strategy, dbDocs, swaggerDocs) => {
        return await post('agents/optimized/validate-strategy', {
            strategy,
            dbDocs,
            swaggerDocs
        });
    },

    // Cadena completa optimizada
    fullChain: async (userId, objective, fechaClaveId, context) => {
        return await post('agents/optimized/full-chain', {
            userId,
            objective,
            fechaClaveId,
            context
        });
    },

    // Generar estrategias A/B
    generateABStrategies: async (baseStrategy, variations) => {
        return await post('agents/optimized/ab-strategies', {
            baseStrategy,
            variations
        });
    }
};

// ===== ORQUESTACIÓN OPTIMIZADA =====
export const orchestrationOptimized = {
    // Ejecutar cadena completa optimizada
    runFullChain: async (userId, objective, fechaClaveId, context) => {
        return await post('agents/optimized/orchestration/full-chain', {
            userId,
            objective,
            fechaClaveId,
            context
        });
    },

    // Ejecutar con retry automático
    runWithRetry: async (operation, maxRetries = 3) => {
        return await post('agents/optimized/orchestration/with-retry', {
            operation,
            maxRetries
        });
    }
};

// ===== GESTIÓN DE ESTRATEGIAS =====
export const strategyManagement = {
    // Obtener estrategia pendiente
    getPendingStrategy: async (marcaId) => {
        return await get(`estrategias/ultima?marcaId=${marcaId}`);
    },

    // Actualizar estado de estrategia
    updateStrategyStatus: async (strategyId, status) => {
        return await patch(`estrategias/${strategyId}/estado`, { estado: status });
    },

    // Crear estrategia en sistema
    createInSystem: async (userId, finalStrategy) => {
        return await post('agents/create-in-system', {
            userId,
            finalStrategy
        });
    }
};

// ===== MÉTRICAS Y MONITOREO =====
export const metricsOptimized = {
    // Obtener métricas de rendimiento
    getPerformanceMetrics: async (userId) => {
        return await get(`agents/optimized/metrics/performance/${userId}`);
    },

    // Obtener métricas de tokens
    getTokenMetrics: async (userId) => {
        return await get(`agents/optimized/metrics/tokens/${userId}`);
    },

    // Obtener métricas de errores
    getErrorMetrics: async (userId) => {
        return await get(`agents/optimized/metrics/errors/${userId}`);
    }
};

// ===== UTILIDADES =====
export const agentsUtils = {
    // Verificar si hay campaña pendiente
    checkPendingCampaign: async (marcaId) => {
        try {
            const pending = await strategyManagement.getPendingStrategy(marcaId);
            return pending && pending._id && pending.estado === 'pendiente';
        } catch (error) {
            console.error('Error verificando campaña pendiente:', error);
            return false;
        }
    },

    // Extraer ID de estrategia
    getStrategyId: (strategy) => {
        if (!strategy || !strategy._id) return null;
        if (typeof strategy._id === 'object' && strategy._id.$oid) return strategy._id.$oid;
        return String(strategy._id);
    },

    // Formatear contexto para IA
    formatContext: (context) => {
        return {
            tipoNegocio: context.tipoNegocio || 'Comercio',
            pais: context.pais || 'España',
            presupuesto: context.presupuesto || '5000€',
            tiempoLimite: context.tiempoLimite || '3 meses',
            ...context
        };
    }
};

const agentsHelper = {
    agentsOptimized,
    strategiesOptimized,
    orchestrationOptimized,
    strategyManagement,
    metricsOptimized,
    agentsUtils
};

export default agentsHelper; 